/**
 * a2-hp.js — homepage redesign (templates/index.hp.json). Spec: homepage-handoff 02-BEHAVIOUR.
 *   [data-a2-hp-media]  image-or-video slots (§1): hero, mission tiles, Find your Stretch, Versoform, quote band
 *   [data-a2-hp-rail]   best-sellers rail (§8): arrows, progress bar
 *   [data-a2-hp-track]  click analytics (§11), plus rail product clicks and hp_view
 * The matcher, press cycling and range tabs reuse assets/a2-lp.js (data-a2-lp-* contracts, events renamed hp_*).
 * Each root initialises on its own and is null-safe, so sections can be removed / reordered in the editor;
 * shopify:section:load re-initialises, shopify:section:unload cleans up.
 */
(function () {
  'use strict';
  if (window.A2HP) return;

  var mqDesktop = window.matchMedia('(min-width: 1024px)');
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var reduceData = window.matchMedia('(prefers-reduced-data: reduce)');

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }
  function track(name, data) {
    data = Object.assign({ template: 'index' }, data || {});
    try { (window.dataLayer = window.dataLayer || []).push(Object.assign({ event: name }, data)); } catch (e) {}
    try { if (window.Shopify && Shopify.analytics && Shopify.analytics.publish) Shopify.analytics.publish(name, data); } catch (e) {}
  }

  /* ---------- §1 Media slots: video only once it actually plays ---------- */
  var slots = [];
  var reported = {};
  function fallback(el, reason) {
    var v = el._v;
    if (v) { try { v.pause(); } catch (e) {} v.removeAttribute('src'); try { v.load(); } catch (e) {} }
    el._failed = true;
    el.classList.remove('is-playing');
    var slot = el.getAttribute('data-slot') || '';
    if (!reported[slot]) { reported[slot] = true; track('hp_media_fallback', { slot: slot, reason: reason }); }
  }
  // The 1.5s "never started" window (Low Power Mode) counts from when the video could play, not from when it
  // started downloading: two 1080p hero streams at page load routinely need longer than 1.5s to buffer, and a slow
  // download must not cost the visitor the video. It only runs while the slot is on screen (scrolling past pauses
  // the video, which is not a failure). Until it plays, the image shows anyway.
  function arm(el) {
    clearTimeout(el._timer);
    el._timer = setTimeout(function () { if (!el._ok && el._visible && !document.hidden) fallback(el, 'lowpower'); }, 1500);
  }
  function play(el) {
    var p = el._v.play();
    if (p && p.catch) p.catch(function (err) {
      // AbortError = our own pause() (slot scrolled out before it started) — retried when it scrolls back in.
      if (el._ok || el._failed || (err && err.name === 'AbortError')) return;
      clearTimeout(el._timer);
      fallback(el, 'lowpower');
    });
  }
  // A video section setting only gives Liquid Shopify's top MP4 (1080p), but the 720p and 480p renditions sit next
  // to it under a predictable name (each 1080p bitrate has its own 720p/480p pair). Use the smallest one that still
  // covers the slot on screen, capped at 720p: the video is cover-cropped, density counts up to 1.5x and up to 1.65x
  // upscaling is fine for a muted loop. Slow connections get 480p. If a rendition fails to load, step up to the next
  // one, ending at the URL Liquid gave us.
  var TIERS = { '7.2': ['4.5', '1.5'], '4.8': ['3.0', '1.2'], '2.5': ['1.6', '0.9'] };
  var MAX_DPR = 1.5, MAX_UPSCALE = 1.65;
  var ALWAYS_480 = { quote_band: true, tile_1: true, tile_2: true, tile_3: true }; // low-fi quote band, small 3-up tiles
  var ALWAYS_FULL = { find_your_stretch: true }; // short clip, needs to stay sharp: always the top (1080p) file
  function sources(el, v) {
    var desk = mqDesktop.matches;
    var url = desk ? (v.dataset.hd || v.dataset.sd) : (v.dataset.hdM || v.dataset.sdM || v.dataset.sd);
    if (!url) return [];
    var m = url.match(/\.HD-1080p-([\d.]+)Mbps/), t = m && TIERS[m[1]];
    if (!t || ALWAYS_FULL[el.getAttribute('data-slot')]) return [url];
    var box = el.getBoundingClientRect();
    var ar = parseFloat(desk ? v.dataset.ar : (v.dataset.arM || v.dataset.ar)) || 0;
    var need = Math.max(box.height, ar ? box.width / ar : 0) * Math.min(window.devicePixelRatio || 1, MAX_DPR);
    var c = navigator.connection, slow = !!(c && /2g|3g/.test(c.effectiveType || ''));
    var r720 = url.replace(m[0], '.HD-720p-' + t[0] + 'Mbps'), r480 = url.replace(m[0], '.SD-480p-' + t[1] + 'Mbps');
    if (slow || ALWAYS_480[el.getAttribute('data-slot')] || need <= 480 * MAX_UPSCALE) return [r480, r720, url];
    return [r720, url];
  }
  function start(el) {
    var v = el._v;
    if (el._failed) return;
    if (el._started) {
      if (v.paused && !document.hidden) { play(el); if (!el._ok && v.readyState >= 3) arm(el); }
      return;
    }
    el._started = true;
    var list = sources(el, v), i = 0;
    if (!list.length) { fallback(el, 'error'); return; }
    v.src = list[0];
    v.addEventListener('canplay', function () {
      if (el._ok || el._failed || !el._visible || el._timer) return;
      arm(el);
    });
    v.addEventListener('playing', function () {
      clearTimeout(el._timer);
      if (el._failed) return;
      el._ok = true;
      el.classList.add('is-playing');
    });
    v.addEventListener('error', function () {
      if (el._ok || el._failed) return;
      clearTimeout(el._timer); el._timer = 0;
      if (++i < list.length) { v.src = list[i]; if (el._visible && !document.hidden) play(el); return; }
      fallback(el, 'error');
    });
    play(el);
  }
  var mediaIO = 'IntersectionObserver' in window ? new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      var el = e.target;
      el._visible = e.isIntersecting;
      if (e.isIntersecting) start(el);
      else {
        clearTimeout(el._timer); el._timer = 0;
        if (el._v && !el._v.paused) el._v.pause();
      }
    });
  }, { rootMargin: '200px 0px' }) : null;
  function initMedia(scope) {
    $$('[data-a2-hp-media]', scope).forEach(function (el) {
      if (el._a2hp) return; el._a2hp = true;
      var v = $('video', el);
      if (!v) return;
      el._v = v;
      var saveData = !!(navigator.connection && navigator.connection.saveData);
      if (reduceMotion.matches) return fallback(el, 'reducedmotion');
      if (saveData || reduceData.matches) return fallback(el, 'savedata');
      slots.push(el);
      if (mediaIO) mediaIO.observe(el); else start(el);
    });
  }
  document.addEventListener('visibilitychange', function () {
    slots.forEach(function (el) {
      if (!el._v || el._failed || !el._started) return;
      if (document.hidden) { clearTimeout(el._timer); el._timer = 0; el._v.pause(); }
      else if (el._visible) start(el);
    });
  });

  /* ---------- §8 Best-sellers rail ---------- */
  function initRail(root) {
    var track_ = $('[data-rail-track]', root);
    if (!track_) return;
    var prev = $('[data-rail-prev]', root), next = $('[data-rail-next]', root);
    var arrows = $('[data-rail-arrows]', root), bar = $('[data-rail-bar]', root), thumb = $('[data-rail-thumb]', root);
    var raf = 0;
    function step() {
      var card = $('.a2-hp-card', track_);
      if (!card) return track_.clientWidth;
      var gap = parseFloat(getComputedStyle(track_).columnGap) || 0;
      return card.getBoundingClientRect().width + gap;
    }
    function update() {
      raf = 0;
      var max = track_.scrollWidth - track_.clientWidth, x = track_.scrollLeft;
      var fits = max <= 2;
      if (arrows) arrows.hidden = fits;
      if (prev) prev.disabled = x <= 2;
      if (next) next.disabled = x >= max - 2;
      if (bar && thumb) {
        bar.hidden = fits;
        if (!fits) {
          var w = track_.clientWidth / track_.scrollWidth;
          var bw = bar.clientWidth;
          thumb.style.setProperty('--w', (w * 100) + '%');
          thumb.style.setProperty('--x', ((x / max) * (bw - bw * w)) + 'px');
        }
      }
    }
    function onScroll() { if (!raf) raf = requestAnimationFrame(update); }
    function go(dir) {
      track_.scrollBy({ left: dir * step(), behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      track('hp_rail_arrow', { dir: dir > 0 ? 'next' : 'prev' });
    }
    if (prev) prev.addEventListener('click', function () { go(-1); });
    if (next) next.addEventListener('click', function () { go(1); });
    track_.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    update();
    window.addEventListener('load', update);
    root._a2hpCleanup = function () { window.removeEventListener('resize', onScroll); };
  }

  /* Colour dots: the theme's swatch colours (assets/swatches.json), as the A2 product page paints them. */
  var swatchData = null;
  function paintSwatches(scope) {
    var dots = $$('[data-a2-swatch-name]', scope).filter(function (d) { return !d.style.getPropertyValue('--a2-swatch'); });
    if (!dots.length || !window.theme || !theme.assets || !theme.assets.swatches) return;
    swatchData = swatchData || fetch(theme.assets.swatches).then(function (r) { return r.json(); }).then(function (d) {
      var map = {};
      (d.colors || []).forEach(function (o) { var k = Object.keys(o)[0]; map[k.toLowerCase().replace(/\s/g, '')] = o[k]; });
      return map;
    }).catch(function () { return {}; });
    swatchData.then(function (map) {
      var base = theme.assets.base || '/';
      dots.forEach(function (d) {
        var v = map[d.getAttribute('data-a2-swatch-name').toLowerCase().replace(/\s/g, '')];
        if (v) d.style.setProperty('--a2-swatch', /\.(jpe?g|png|svg|gif|webp)$/i.test(v) ? 'url(' + base + v + ')' : v);
      });
    });
  }

  /* ---------- §11 Analytics ---------- */
  document.addEventListener('click', function (e) {
    var t = e.target.closest && e.target.closest('.ys.a2-hp [data-a2-hp-track]');
    if (t) {
      var data = {};
      if (t.hasAttribute('data-panel')) data.panel = t.getAttribute('data-panel');
      track(t.getAttribute('data-a2-hp-track'), data);
      return;
    }
    var p = e.target.closest && e.target.closest('.ys.a2-hp [data-a2-hp-product]');
    if (p) track('hp_rail_product', { product_id: p.getAttribute('data-a2-hp-product'), position: +p.getAttribute('data-pos') });
  });

  function init(scope) {
    scope = scope || document;
    initMedia(scope);
    $$('[data-a2-hp-rail]', scope).forEach(function (r) { if (!r._a2hp) { r._a2hp = true; initRail(r); } });
    paintSwatches(scope);
  }
  window.A2HP = { init: init, track: track };

  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    $$('.ys.a2-hp', e.target).forEach(function (r) { if (r._a2hpCleanup) r._a2hpCleanup(); });
    $$('[data-a2-hp-media]', e.target).forEach(function (el) {
      if (mediaIO) mediaIO.unobserve(el);
      var i = slots.indexOf(el); if (i > -1) slots.splice(i, 1);
    });
  });

  function boot() { init(); track('hp_view', {}); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();
